/**
 * Account · Billing (immersive account view, tab 3 of 5).
 *
 * A gradient current-plan banner (plan, price, renews date, manage / upgrade),
 * a usage-this-month row of plain meters (call minutes / conversations / active
 * genies), then a payment-method + invoice-history block, with the live call
 * purchases and active phone numbers folded in below.
 *
 * Re-skins the live SubscriptionSection + CallPurchasesSection +
 * ActivePhoneNumbersSection into the shared AcctPageHead / AcctCard rhythm. All
 * data via the REAL useSubscription() / useCallPurchases() /
 * usePhoneSubscription() / useUserAgents(); Manage plan opens the existing
 * billing portal (useBillingPortal); on return the existing
 * useCheckSubscriptionStatus re-syncs. No business logic is re-implemented.
 *
 * NOTE: call-minute and conversation totals have no per-account read yet, so
 * those two meters use realistic STUB caps shaped to the eventual usage
 * contract; the active-genies meter and everything else is REAL.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Mail,
  Minus,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useSubscription } from "@/contexts/billing/subscription";
import { useCheckSubscriptionStatus } from "@/hooks/billing/useCheckSubscriptionStatus";
import { useBillingPortal } from "@/hooks/billing/useBillingPortal";
import { useCallPurchases } from "@/hooks/billing/useCallPurchases";
import { usePhoneSubscription } from "@/hooks/billing/usePhoneSubscription";
import { useUserAgents } from "@/hooks/agent/useAgents";
import { useCurrency } from "@/contexts/currency/CurrencyContext";
import { formatPrice } from "@/config/pricing";
import {
  AcctCard,
  AcctPageHead,
  ACCT_BRAND as B,
} from "@/components/voice-account/VoiceAccountDetail";

const SECTION_LABEL = "text-xs font-bold tracking-[0.6px] uppercase text-gray-500";

function fmtDate(value?: string | null): string {
  if (!value) return "N/A";
  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Plain usage meter — label, big used/cap figure, brand-tinted progress bar. */
const UsageMeter = ({
  label,
  used,
  cap,
  unit,
  tint,
  note,
}: {
  label: string;
  used: number;
  cap: number;
  unit?: string;
  tint: string;
  note?: string;
}) => {
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-[12.5px] font-semibold text-[#64748B]">{label}</span>
        {note && (
          <span className="text-[10px] font-bold uppercase tracking-[0.4px] text-gray-400">
            {note}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-extrabold text-[#222B45] tracking-[-0.5px]">
          {used.toLocaleString()}
        </span>
        <span className="text-[13px] text-[#64748B]">
          / {cap.toLocaleString()} {unit}
        </span>
      </div>
      <div className="h-[7px] rounded-full bg-genie-background overflow-hidden border border-genie-border/60">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: tint }}
        />
      </div>
    </div>
  );
};

const BillingTab = () => {
  const { subscription, isLoading } = useSubscription();
  const { checkSubscriptionStatus } = useCheckSubscriptionStatus();
  const { openBillingPortal, loading: portalLoading } = useBillingPortal();
  const { currency, currencyConfig } = useCurrency();
  const [searchParams] = useSearchParams();

  /* ── call purchases (re-skin of CallPurchasesSection) ── */
  const { callPurchases, isLoadingPurchases, cancelCalls, isCanceling } = useCallPurchases();

  /* ── active phone numbers (re-skin of ActivePhoneNumbersSection) ── */
  const { phoneSubscriptions, removePhoneNumber } = usePhoneSubscription();
  const [showCancelledPhones, setShowCancelledPhones] = useState(false);
  const [releasingPhoneId, setReleasingPhoneId] = useState<string | null>(null);

  /* ── genies (real cap for the usage meter) ── */
  const { data: agents = [] } = useUserAgents();

  /* re-sync subscription on return from the billing portal (house behaviour) */
  useEffect(() => {
    if (searchParams.get("from") === "billing" && subscription?.stripe_id) {
      checkSubscriptionStatus(subscription.stripe_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.stripe_id]);

  const activePhones = phoneSubscriptions.filter((p) => p.status !== "cancelled");
  const cancelledPhones = phoneSubscriptions.filter((p) => p.status === "cancelled");

  const handleManagePlan = async () => {
    if (!subscription?.stripe_id) {
      toast.error("Set up billing first to manage your plan.");
      return;
    }
    try {
      await openBillingPortal();
    } catch (err: any) {
      toast.error(`We couldn't open the billing portal: ${err.message}`);
    }
  };

  const handleReleasePhone = async (phone: any) => {
    try {
      setReleasingPhoneId(phone.id);
      await removePhoneNumber(phone.phone_number);
      toast.success(`Phone number ${phone.phone_number} released successfully.`);
    } catch {
      toast.error("We couldn't release that phone number. Please try again.");
    } finally {
      setReleasingPhoneId(null);
    }
  };

  const handleCancelCalls = async (purchase: any) => {
    try {
      const result = await cancelCalls(purchase.agent_id);
      if (result.success) {
        toast.success("Extra calls reduced successfully.");
      } else {
        toast.error(result.message || "We couldn't reduce those calls.");
      }
    } catch {
      toast.error("We couldn't reduce those calls. Please try again.");
    }
  };

  /* ── derived plan + usage ── */
  const isTrial = subscription?.status === "trialing";
  const monthlyPrice = currencyConfig.displayPrices.professional;
  const planName = subscription ? "Voice Pro" : "Free plan";
  const renewLabel = subscription?.ends_at
    ? `Renews ${fmtDate(subscription.ends_at)} · billed monthly`
    : isTrial && subscription?.trial_ends_at
      ? `Trial ends ${fmtDate(subscription.trial_ends_at)}`
      : "Billed monthly";

  const genieCap = useMemo(
    () => 1 + (subscription?.quantity || 0) + (subscription?.extra_quantity || 0),
    [subscription],
  );
  const geniesUsed = Math.min(agents.length, genieCap);

  // STUB until BE lands: per-account call-minute + conversation usage reads.
  const usageMeters = [
    { label: "Call minutes", used: 0, cap: 3000, unit: "min", tint: B.blue, note: "soon" },
    { label: "Conversations", used: 0, cap: 1000, unit: "", tint: B.purple, note: "soon" },
    { label: "Active genies", used: geniesUsed, cap: genieCap, unit: "", tint: B.pink },
  ];

  /* ── loading skeleton ── */
  if (isLoading) {
    return (
      <>
        <AcctPageHead
          icon="card"
          title="Billing"
          subtitle="Your plan, what you've used this month, and past invoices."
          tint={B.pink}
        />
        <div className="h-[118px] rounded-[18px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] opacity-40 animate-pulse mb-5" />
        <AcctCard label="Usage this month" className="mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-2.5">
                <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
                <div className="h-7 w-20 rounded bg-gray-100 animate-pulse" />
                <div className="h-[7px] rounded-full bg-gray-100 animate-pulse" />
              </div>
            ))}
          </div>
        </AcctCard>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-5">
          {[0, 1].map((i) => (
            <AcctCard key={i}>
              <div className="space-y-3">
                <div className="h-3 w-28 rounded bg-gray-100 animate-pulse" />
                <div className="h-[46px] rounded-xl bg-gray-100 animate-pulse" />
                <div className="h-[46px] rounded-xl bg-gray-100 animate-pulse" />
              </div>
            </AcctCard>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <AcctPageHead
        icon="card"
        title="Billing"
        subtitle="Your plan, what you've used this month, and past invoices."
        tint={B.pink}
      />

      {/* ── Hero plan banner ── */}
      <div className="relative rounded-[18px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-[0_14px_34px_-16px_rgba(255,77,184,0.5)] px-6 py-[22px] mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold tracking-[0.6px] uppercase text-white/80">Current plan</p>
          <div className="flex items-baseline gap-2.5 mt-1 flex-wrap">
            <span className="text-[26px] font-extrabold text-white tracking-[-0.5px]">
              {planName}
            </span>
            {subscription && (
              <span className="text-[15px] font-bold text-white/90">
                {formatPrice(monthlyPrice, currency)} / month
              </span>
            )}
          </div>
          <p className="text-[13px] text-white/[0.88] mt-1">{renewLabel}</p>
        </div>
        <div className="flex gap-2.5 flex-shrink-0">
          {subscription ? (
            <>
              <button
                type="button"
                onClick={handleManagePlan}
                disabled={portalLoading || !subscription.stripe_id}
                className="h-10 px-4 rounded-full bg-white/[0.18] inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-white transition-all hover:bg-white/25 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {portalLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {portalLoading ? "Opening..." : "Manage plan"}
              </button>
              <Link
                to="/pricing"
                className="h-10 px-4 rounded-full bg-white inline-flex items-center justify-center text-[13.5px] font-extrabold text-genie-secondary transition-all hover:opacity-90"
              >
                Upgrade
              </Link>
            </>
          ) : (
            <Link
              to="/pricing"
              className="h-10 px-4 rounded-full bg-white inline-flex items-center justify-center text-[13.5px] font-extrabold text-genie-secondary transition-all hover:opacity-90"
            >
              View plans
            </Link>
          )}
        </div>
      </div>

      {/* ── Usage this month ── */}
      <AcctCard label="Usage this month" className="mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {usageMeters.map((u) => (
            <UsageMeter key={u.label} {...u} />
          ))}
        </div>
      </AcctCard>

      {/* ── Payment method + invoices ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.25fr] gap-5 items-start mb-5">
        {/* payment method */}
        <AcctCard label="Payment method">
          {subscription?.stripe_id ? (
            <>
              <div className="flex items-center gap-3.5">
                <div
                  className="w-12 h-[34px] rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${B.blue}14` }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={B.blue}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14.5px] font-bold text-[#222B45]">Card on file</p>
                  <p className="text-[12.5px] text-[#64748B]">
                    Managed securely in the billing portal
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2.5 mt-[18px]">
                <button
                  type="button"
                  onClick={handleManagePlan}
                  disabled={portalLoading}
                  className="h-10 px-4 rounded-full border border-transparent inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-genie-primary bg-genie-primary/10 transition-all hover:opacity-90 disabled:opacity-60"
                >
                  <Pencil className="w-[15px] h-[15px]" />
                  Update card
                </button>
                <button
                  type="button"
                  onClick={handleManagePlan}
                  disabled={portalLoading}
                  className="h-10 px-4 rounded-full border border-genie-border inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-[#64748B] bg-white transition-all hover:opacity-90 disabled:opacity-60"
                >
                  <Mail className="w-[15px] h-[15px] text-genie-primary" />
                  Billing email
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-6 rounded-xl border border-dashed border-genie-border bg-genie-background">
              <div
                className="w-11 h-11 mx-auto rounded-[13px] flex items-center justify-center mb-3"
                style={{ backgroundColor: `${B.blue}1a` }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={B.blue}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </div>
              <p className="text-[13.5px] font-bold text-[#222B45]">No payment method yet</p>
              <p className="text-[12.5px] text-[#64748B] mt-1 px-6 mb-4">
                Choose a plan to add a card and unlock premium features.
              </p>
              <Link
                to="/pricing"
                className="inline-flex h-9 px-4 rounded-full items-center justify-center text-[13px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90"
              >
                View pricing plans
              </Link>
            </div>
          )}
        </AcctCard>

        {/* invoice history */}
        <AcctCard label="Invoice history">
          {subscription?.stripe_id ? (
            <>
              <button
                type="button"
                onClick={handleManagePlan}
                disabled={portalLoading}
                className="w-full flex items-center gap-3 py-3.5 text-left transition-colors hover:bg-genie-background/60 rounded-lg -mx-2 px-2 disabled:opacity-60"
              >
                <div className="w-[34px] h-[34px] rounded-[9px] bg-genie-background flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-[#64748B]" />
                </div>
                <span className="flex-1 min-w-0 text-[13.5px] font-semibold text-[#222B45]">
                  View all invoices
                </span>
                <span className="text-[12.5px] text-[#64748B]">in the billing portal</span>
                <ChevronRight className="w-[15px] h-[15px] text-gray-400 flex-shrink-0" />
              </button>
              <p className="text-[11.5px] text-gray-500 mt-2">
                Your full invoice history, receipts, and billing details live in the secure billing
                portal.
              </p>
            </>
          ) : (
            <div className="text-center py-6 rounded-xl border border-dashed border-genie-border bg-genie-background">
              <div className="w-11 h-11 mx-auto rounded-[13px] bg-genie-primary/10 flex items-center justify-center mb-3">
                <FileText className="w-5 h-5 text-genie-primary" />
              </div>
              <p className="text-[13.5px] font-bold text-[#222B45]">No invoices yet</p>
              <p className="text-[12.5px] text-[#64748B] mt-1 px-6">
                Invoices will appear here once you start a paid plan.
              </p>
            </div>
          )}
        </AcctCard>
      </div>

      {/* ── Call purchases (re-skin of CallPurchasesSection) ── */}
      <AcctCard className="mb-5">
        <div className="flex items-center justify-between mb-4">
          <p className={SECTION_LABEL}>Call purchases</p>
          {subscription?.stripe_id && (
            <button
              type="button"
              onClick={handleManagePlan}
              className="h-7 px-3 rounded-full border border-genie-border inline-flex items-center gap-1.5 text-xs font-bold text-genie-primary hover:bg-genie-primary/5 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add calls
            </button>
          )}
        </div>

        {isLoadingPurchases ? (
          <div className="flex flex-col gap-2.5">
            {[0, 1].map((i) => (
              <div key={i} className="h-[58px] rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : callPurchases.length === 0 ? (
          <div className="text-center py-6 rounded-xl border border-dashed border-genie-border bg-genie-background">
            <div className="w-11 h-11 mx-auto rounded-[13px] bg-genie-primary/10 flex items-center justify-center mb-3">
              <Plus className="w-5 h-5 text-genie-primary" />
            </div>
            <p className="text-[13.5px] font-bold text-[#222B45]">No extra calls purchased</p>
            <p className="text-[12.5px] text-[#64748B] mt-1 px-6">
              Top up a genie's daily call limit any time from the billing portal.
            </p>
          </div>
        ) : (
          <div className="flex flex-col">
            {callPurchases.map((purchase: any, i) => (
              <div
                key={purchase.id}
                className={`flex items-center gap-3 py-3 ${
                  i === 0 ? "" : "border-t border-genie-border/60"
                }`}
              >
                <div className="flex-1 min-w-0">
                  {purchase.agents?.name && (
                    <p className="text-[12.5px] font-bold text-genie-primary truncate">
                      {purchase.agents.name}
                    </p>
                  )}
                  <p className="text-[13.5px] font-semibold text-[#222B45]">
                    {purchase.quantity} calls purchased
                  </p>
                  <p className="text-[12px] text-gray-500">{fmtDate(purchase.created_at)}</p>
                </div>
                <span
                  className="h-6 px-2.5 rounded-full inline-flex items-center text-[11px] font-bold capitalize flex-shrink-0"
                  style={{ backgroundColor: `${B.green}1f`, color: B.green }}
                >
                  {purchase.status || "active"}
                </span>
                {purchase.status === "active" && (
                  <button
                    type="button"
                    onClick={() => handleCancelCalls(purchase)}
                    disabled={isCanceling}
                    title="Reduce calls"
                    className="w-7 h-7 rounded-full inline-flex items-center justify-center text-gray-400 hover:text-genie-secondary hover:bg-genie-secondary/10 transition-colors flex-shrink-0 disabled:opacity-50"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </AcctCard>

      {/* ── Active phone numbers (re-skin of ActivePhoneNumbersSection) ── */}
      <AcctCard label="Active phone numbers">
        {activePhones.length === 0 ? (
          <div className="text-center py-6 rounded-xl border border-dashed border-genie-border bg-genie-background">
            <div className="w-11 h-11 mx-auto rounded-[13px] bg-genie-primary/10 flex items-center justify-center mb-3">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-genie-primary"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.74a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <p className="text-[13.5px] font-bold text-[#222B45]">No active phone numbers</p>
            <p className="text-[12.5px] text-[#64748B] mt-1 px-6">
              Provision a number from a genie's settings to start taking calls.
            </p>
          </div>
        ) : (
          <div className="flex flex-col">
            {activePhones.map((phone: any, i) => (
              <div
                key={phone.id}
                className={`flex items-center gap-3 py-3 ${
                  i === 0 ? "" : "border-t border-genie-border/60"
                }`}
              >
                <div className="flex-1 min-w-0">
                  {phone.agents?.name && (
                    <p className="text-[12.5px] font-bold text-genie-primary truncate">
                      {phone.agents.name}
                    </p>
                  )}
                  <p className="text-[13.5px] font-semibold text-[#222B45]">{phone.phone_number}</p>
                  <p className="text-[12px] text-gray-500 capitalize">Type: {phone.phone_type}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleReleasePhone(phone)}
                  disabled={releasingPhoneId === phone.id}
                  className="h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-[12.5px] font-bold text-genie-secondary hover:bg-genie-secondary/10 transition-colors flex-shrink-0 disabled:opacity-50"
                >
                  {releasingPhoneId === phone.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                  {releasingPhoneId === phone.id ? "Releasing..." : "Release"}
                </button>
              </div>
            ))}
          </div>
        )}

        {cancelledPhones.length > 0 && (
          <div className="mt-4 pt-4 border-t border-genie-border/60">
            <button
              type="button"
              onClick={() => setShowCancelledPhones((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <span className="text-[12.5px] font-bold text-[#64748B]">
                Cancelled numbers ({cancelledPhones.length})
              </span>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${
                  showCancelledPhones ? "rotate-180" : ""
                }`}
              />
            </button>
            {showCancelledPhones && (
              <div className="flex flex-col mt-2">
                {cancelledPhones.map((phone: any, i) => (
                  <div
                    key={phone.id}
                    className={`flex items-center gap-3 py-3 ${
                      i === 0 ? "" : "border-t border-genie-border/60"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-semibold text-gray-500">
                        {phone.phone_number}
                      </p>
                      <p className="text-[12px] text-gray-400 capitalize">
                        Type: {phone.phone_type}
                      </p>
                    </div>
                    <span className="h-6 px-2.5 rounded-full inline-flex items-center text-[11px] font-bold bg-gray-100 text-gray-500 flex-shrink-0">
                      Cancelled
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </AcctCard>
    </>
  );
};

export default BillingTab;
